import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime, renderFormattedText } from "../../utils/HelperFunctions";
import { ArrowLeftIcon, DocumentTextIcon, EyeIcon, GlobeAltIcon, MegaphoneIcon } from "@heroicons/react/24/outline";
import { ProgressSpinner } from "primereact/progressspinner";

const JobView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [record, setRecord] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get(`/job/admin/${id}`);
                if (response?.data?.success) {
                    setRecord(response.data.data);
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) {
        return (
            <div className="unlist-margin flex justify-center items-center" style={{ minHeight: "300px" }}>
                <ProgressSpinner />
            </div>
        );
    }

    const locationText = record
        ? (record.joblocationcity || (typeof record.jobLocation === "string" ? record.jobLocation : record.jobLocation?.formattedAddress) || "-")
        : "-";

    return (
        <div className="unlist-margin">
            <div className="back-button mb-2" onClick={() => navigate("/admin/jobdesc")}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>

            <Card>
                <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
                    <h2 className="m-0">View Job</h2>
                    <div className="flex gap-2 flex-wrap">
                        <Button
                            label="View Public Page"
                            icon={<GlobeAltIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            outlined
                            severity="secondary"
                            onClick={() => window.open(`https://sagarsamaj.in/job/${id}`, "_blank")}
                        />
                        <Button
                            label={`Viewers (${record?.viewCount ?? 0})`}
                            icon={<EyeIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            outlined
                            onClick={() => navigate(`/admin/jobdesc/viewers/${id}`)}
                        />
                        <Button
                            label="Create Advertisement"
                            icon={<MegaphoneIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            severity="help"
                            outlined
                            onClick={() => navigate("/admin/advertisement/add", {
                                state: {
                                    prefill: {
                                        company_name: record?.companyName || record?.designation || "",
                                        mobile_number: record?.contactPersonMobile || "",
                                        email: record?.contactPersonEmail || "",
                                        city: record?.joblocationcity || "",
                                        userid: record?.userid || "",
                                        ownerName: record?.ownerName || "",
                                        imageUrl: record?.companyLogoUrl || "",
                                        short_description: "Job Alert",
                                        long_description: record?.roleResponsibility ? record.roleResponsibility : "",
                                    }
                                }
                            })}
                        />
                        <Button label="Edit" onClick={() => navigate(`/admin/jobdesc/edit/${id}`)} />
                    </div>
                </div>

                {record && (
                    <div className="grid grid-cols-12 gap-4">

                        <div className="col-span-12 flex items-center gap-4">
                            {record.companyLogoUrl ? (
                                <img src={record.companyLogoUrl} alt={record.companyName} style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "10px", border: "1px solid #e5e7eb" }} />
                            ) : (
                                <div style={{ width: "80px", height: "80px", borderRadius: "10px", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <DocumentTextIcon style={{ width: "36px", height: "36px", color: "#d1d5db" }} />
                                </div>
                            )}
                            <div>
                                <h3 className="m-0">{record.designation}</h3>
                                <p className="m-0 text-gray-500">{record.companyName || "-"}</p>
                                <Tag className="mt-1" value={record.isactive ? "Active" : "Inactive"} severity={record.isactive ? "success" : "danger"} />
                            </div>
                        </div>

                        <div className="lg:col-span-6 col-span-12"><strong>Posted By:</strong> {record.ownerName || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>No. of Vacancies:</strong> {record.noOfVacancies ?? "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Location:</strong> {locationText}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Salary:</strong> {record.salary || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Education:</strong> {record.education || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Experience:</strong> {record.experience || "-"}</div>
                        <div className="col-span-12">
                            <strong>Role & Responsibility:</strong>
                            <p className="m-0 mt-1" style={{ whiteSpace: "pre-wrap", color: "#374151", lineHeight: 1.6 }}>
                                {renderFormattedText(record.roleResponsibility)}
                            </p>
                        </div>
                        <div className="lg:col-span-6 col-span-12"><strong>Contact Person:</strong> {record.contactPersonName || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Contact Mobile:</strong> {record.contactPersonMobile || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Contact Email:</strong> {record.contactPersonEmail || "-"}</div>
                        <div className="lg:col-span-6 col-span-12">
                            <strong>Created Date:</strong> {formatDateTime(record.createddate)}
                        </div>

                    </div>
                )}
            </Card>
        </div>
    );
};

export default JobView;
